import React from "react";
import { Link } from "react-router-dom";
import {
  UtensilsCrossed,
  TrendingUp,
  Gift,
  Star,
} from "lucide-react";
import { BrandMark } from "../components/BrandMark";
import { BrandMarkLink } from "../components/BrandMarkLink";
import { Divider } from "../components/Divider";
import { PaperGrain } from "../components/PaperGrain";
import { useScrollReveal } from "../hooks/useScrollReveal";
import {
  HERO_IMAGE,
  STORY_IMAGE,
  MENU_ANTIPASTI,
  MENU_PASTA,
  MENU_SECONDI,
  MENU_DOLCI,
  WINE_IMAGE,
} from "../assets/images";

/* -------------------------------------------------------------------------- */
/*  Reusable bits                                                             */
/* -------------------------------------------------------------------------- */

const Reveal = ({ children, className = "", as: Tag = "div", testId, style }) => {
  const [ref, visible] = useScrollReveal();
  return (
    <Tag
      ref={ref}
      data-testid={testId}
      style={style}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
    >
      {children}
    </Tag>
  );
};

const Eyebrow = ({ children, color = "oxblood", testId }) => (
  <p
    data-testid={testId}
    className={`font-body font-semibold text-[11px] tracking-[0.3em] uppercase ${
      color === "gold" ? "text-napoli-gold" : "text-napoli-oxblood"
    }`}
  >
    {children}
  </p>
);

/* -------------------------------------------------------------------------- */
/*  SECTION 1 — Hero                                                          */
/* -------------------------------------------------------------------------- */

const Hero = () => {
  // 5 shimmer dots scattered randomly; positions chosen once, animation-delay
  // staggered so they don't pulse in sync.
  const dots = [
    { top: "18%", left: "12%", delay: "0s" },
    { top: "62%", left: "22%", delay: "1.4s" },
    { top: "30%", left: "78%", delay: "2.6s" },
    { top: "75%", left: "70%", delay: "0.8s" },
    { top: "44%", left: "55%", delay: "3.2s" },
  ];

  return (
    <section
      data-testid="landing-hero"
      className="relative min-h-screen w-full bg-napoli-espresso text-napoli-cream flex flex-col items-center justify-center px-6 overflow-hidden"
    >
      {/* Background image */}
      <img
        src={HERO_IMAGE}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover object-center"
        style={{ filter: "brightness(0.85) saturate(0.9) contrast(1.08)" }}
      />
      {/* Gradient overlay */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-napoli-espresso/75 via-napoli-espresso/55 to-napoli-espresso/90"
      />
      {/* Vignette to keep edges dark while center shows the image */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(31,22,17,0.15) 0%, rgba(31,22,17,0.55) 70%, rgba(31,22,17,0.85) 100%)",
        }}
      />
      {/* Top + bottom gold hairlines */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-napoli-gold/40" />
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-px bg-napoli-gold/40" />

      {/* Shimmer dust */}
      {dots.map((d, i) => (
        <span
          key={i}
          aria-hidden
          className="shimmer-dot pointer-events-none absolute h-1 w-1 rounded-full bg-napoli-gold"
          style={{
            top: d.top,
            left: d.left,
            animationDelay: d.delay,
            boxShadow: "0 0 6px #C5A04D",
          }}
        />
      ))}

      {/* Content stack */}
      <div className="relative z-10 flex flex-col items-center text-center max-w-[720px]">
        <span className="block h-px w-[60px] bg-napoli-gold" aria-hidden />
        <p
          data-testid="hero-microcaps"
          className="mt-6 font-body font-semibold text-[11px] tracking-[0.3em] uppercase"
          style={{ color: "rgba(245,235,218,0.7)" }}
        >
          Brooklyn · Italian · Since 2019
        </p>

        <div className="mt-8">
          <BrandMarkLink size="lg" theme="dark" testId="hero-brand-link" ariaLabel="Napoli — home" />
        </div>

        <p
          data-testid="hero-tagline-italian"
          className="mt-12 font-flourish text-napoli-gold leading-none"
          style={{ fontSize: "clamp(48px, 9vw, 96px)" }}
        >
          Mangia con noi
        </p>
        <p
          data-testid="hero-tagline-english"
          className="mt-3 font-body italic text-napoli-cream text-base sm:text-lg"
          style={{ letterSpacing: "0.05em", color: "rgba(245,235,218,0.85)" }}
        >
          A table is waiting.
        </p>

        <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/signup"
            data-testid="cta-join"
            className="inline-flex items-center justify-center bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[13px] tracking-widest uppercase px-10 py-4 hover:bg-napoli-oxblood-dark transition-colors duration-300"
          >
            Join the Program
          </Link>
          <Link
            to="/login"
            data-testid="cta-signin"
            className="inline-flex items-center justify-center border border-napoli-gold text-napoli-gold font-body font-semibold text-[13px] tracking-widest uppercase px-10 py-4 hover:bg-napoli-gold hover:text-napoli-espresso transition-colors duration-300"
          >
            Sign In
          </Link>
        </div>
        <p className="mt-6 font-body italic text-[12px] text-napoli-cream/60">
          Already a member? Sign in above.
        </p>
      </div>

      {/* Small Sign in pill, top-right */}
      <Link
        to="/login"
        data-testid="hero-signin-link"
        className="absolute top-6 right-6 z-20 font-body text-[12px] tracking-[0.18em] uppercase text-napoli-cream/80 hover:text-napoli-gold transition-colors"
      >
        Sign in
      </Link>

      {/* Scroll indicator */}
      <div
        data-testid="hero-scroll-indicator"
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center z-10"
      >
        <p
          className="font-body font-medium text-[10px] tracking-widest uppercase"
          style={{ color: "rgba(245,235,218,0.6)" }}
        >
          Discover
        </p>
        <span
          aria-hidden
          className="scroll-pulse mt-3 block h-6 w-px bg-napoli-gold"
        />
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/*  SECTION 2 — La Nostra Storia                                              */
/* -------------------------------------------------------------------------- */

const Storia = () => (
  <section
    data-testid="landing-storia"
    className="relative bg-napoli-cream text-napoli-espresso py-16 sm:py-24 px-6 overflow-hidden"
  >
    <PaperGrain />
    <div className="relative z-10 mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <Reveal testId="storia-image-wrap" className="order-2 lg:order-1">
        <figure>
          <div
            className="relative overflow-hidden border border-napoli-gold/40"
            style={{
              aspectRatio: "4 / 5",
              boxShadow: "12px 12px 0 0 rgba(139,30,44,0.12)",
            }}
          >
            <img
              src={STORY_IMAGE}
              alt="A table set for pasta and red wine, overhead view."
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]"
            />
          </div>
          <figcaption className="mt-4 font-body italic text-[12px] text-napoli-espresso-soft">
            A table at Napoli, Williamsburg, Brooklyn.
          </figcaption>
        </figure>
      </Reveal>

      <Reveal testId="storia-copy" className="order-1 lg:order-2">
        <Eyebrow testId="storia-eyebrow">La Nostra Storia</Eyebrow>
        <h2 className="mt-4 font-display font-bold text-napoli-espresso leading-tight text-4xl sm:text-5xl lg:text-[3rem]">
          Where Brooklyn meets Naples.
        </h2>
        <p className="mt-6 font-body text-base sm:text-[17px] text-napoli-espresso-soft leading-relaxed">
          Napoli opened in a converted Williamsburg warehouse in 2019. Our chef,
          Marco Belluci, learned to cook in his grandmother&apos;s kitchen in
          the Spanish Quarter of Naples — a place where every meal is loud,
          every recipe is contested, and no one ever leaves hungry.
        </p>
        <p className="mt-4 font-body text-base sm:text-[17px] text-napoli-espresso-soft leading-relaxed">
          We brought that table to Brooklyn. Heritage menu. Hand-pulled pasta.
          Wine that won&apos;t apologize. And a rewards program built for the
          regulars who become family.
        </p>
        <p
          data-testid="storia-signature"
          className="mt-8 font-flourish text-napoli-gold leading-none"
          style={{ fontSize: "32px" }}
        >
          — Marco Belluci, Chef &amp; Owner
        </p>
      </Reveal>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  SECTION 3 — La Cucina                                                     */
/* -------------------------------------------------------------------------- */

const MENU_CARDS = [
  {
    cat: "ANTIPASTI",
    img: MENU_ANTIPASTI,
    alt: "Burrata, bread and olive oil — antipasti.",
    dishes: ["Burrata di Puglia", "Carpaccio di Manzo", "Bruschetta della Casa"],
  },
  {
    cat: "PRIMI",
    img: MENU_PASTA,
    alt: "Spaghetti pasta on a fork against a dark background.",
    dishes: ["Cacio e Pepe", "Pappardelle al Cinghiale", "Linguine alle Vongole"],
  },
  {
    cat: "SECONDI",
    img: MENU_SECONDI,
    alt: "Sliced bistecca alla fiorentina on a dark wooden board.",
    dishes: ["Bistecca alla Fiorentina", "Branzino al Sale", "Osso Buco"],
  },
  {
    cat: "DOLCI",
    img: MENU_DOLCI,
    alt: "Tiramisù close-up, layered cream and cocoa.",
    dishes: ["Tiramisù della Nonna", "Panna Cotta", "Cannoli Siciliani"],
  },
];

const Cucina = () => (
  <section
    data-testid="landing-cucina"
    className="bg-napoli-espresso text-napoli-cream py-16 sm:py-24 px-6"
  >
    <div className="mx-auto max-w-6xl">
      <Reveal className="text-center">
        <Eyebrow color="gold" testId="cucina-eyebrow">La Cucina</Eyebrow>
        <h2 className="mt-4 font-display font-bold text-napoli-cream text-4xl sm:text-5xl lg:text-[3.5rem]">
          From the kitchen.
        </h2>
        <p className="mt-4 mx-auto max-w-[480px] font-body italic text-base sm:text-lg text-napoli-cream/70">
          A heritage menu, served the way Nonna intended.
        </p>
        <div className="mt-8">
          <Divider color="gold" width={280} />
        </div>
      </Reveal>

      <div
        data-testid="cucina-grid"
        className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {MENU_CARDS.map((card) => (
          <Reveal
            key={card.cat}
            testId={`menu-card-${card.cat.toLowerCase()}`}
            className="group bg-napoli-cream-warm text-napoli-espresso flex flex-col"
          >
            <div className="relative overflow-hidden" style={{ aspectRatio: "4 / 3" }}>
              <img
                src={card.img}
                alt={card.alt}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]"
              />
            </div>
            <div className="p-6">
              <p className="font-body font-semibold text-[11px] tracking-[0.3em] uppercase text-napoli-oxblood">
                {card.cat}
              </p>
              <ul className="mt-4 space-y-3">
                {card.dishes.map((d, i) => (
                  <li key={d}>
                    <p className="font-display font-semibold text-[17px] text-napoli-espresso">
                      {d}
                    </p>
                    {i < card.dishes.length - 1 ? (
                      <span
                        aria-hidden
                        className="mt-3 block h-px bg-napoli-gold/30"
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-12 text-center">
        <Link
          to="/signup"
          data-testid="cucina-view-menu"
          className="inline-block font-body font-medium text-[13px] tracking-widest uppercase text-napoli-gold border-b border-napoli-gold pb-1 hover:text-napoli-cream hover:border-napoli-cream transition-colors"
        >
          View Full Menu
        </Link>
      </Reveal>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  SECTION 4 — Il Programma                                                  */
/* -------------------------------------------------------------------------- */

const STEPS = [
  {
    Icon: UtensilsCrossed,
    title: "Dine",
    body: "Earn 1 point per dollar at every visit. Show your QR at checkout.",
  },
  {
    Icon: TrendingUp,
    title: "Climb",
    body: "Move from Ospite to Padrone. Higher tiers earn more points and unlock perks.",
  },
  {
    Icon: Gift,
    title: "Redeem",
    body: "Free Bruschetta, Tiramisù, a bottle of Chianti. Or save for the Bistecca Fiorentina.",
  },
];

const Programma = () => (
  <section
    data-testid="landing-programma"
    className="relative bg-napoli-cream-warm text-napoli-espresso py-16 sm:py-24 px-6 overflow-hidden"
  >
    <PaperGrain />
    <div className="relative z-10 mx-auto max-w-[1100px]">
      <Reveal className="text-center">
        <Eyebrow testId="programma-eyebrow">Il Programma</Eyebrow>
        <h2 className="mt-4 font-display font-bold text-napoli-espresso leading-[1.05] text-4xl sm:text-5xl lg:text-[3.25rem]">
          Earn your seat
          <span className="block font-flourish font-normal text-napoli-oxblood mt-2 text-[2.125rem] sm:text-5xl lg:text-6xl leading-none">
            at the family table.
          </span>
        </h2>
        <p className="mt-6 mx-auto max-w-[600px] font-body italic text-base sm:text-[17px] text-napoli-espresso-soft leading-relaxed">
          Heritage menu. Candle-lit room. No-attitude service. Members earn
          points on every visit, climb four tiers, and unlock perks worthy of
          la famiglia.
        </p>
      </Reveal>

      <div
        data-testid="programma-steps"
        className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8"
      >
        {STEPS.map(({ Icon, title, body }, i) => (
          <Reveal
            key={title}
            testId={`programma-step-${i + 1}`}
            className="flex flex-col items-center text-center"
          >
            <div
              className="flex items-center justify-center bg-napoli-espresso border border-napoli-gold rounded-full"
              style={{ width: 80, height: 80 }}
            >
              <Icon size={32} strokeWidth={1.4} className="text-napoli-gold" />
            </div>
          <h3 className="mt-6 font-body font-bold text-[14px] tracking-[0.2em] uppercase text-napoli-espresso">
              {title}
            </h3>
            <p className="mt-3 font-body text-[14px] text-napoli-espresso-soft leading-relaxed max-w-xs">
              {body}
            </p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  SECTION 5 — I Livelli                                                     */
/* -------------------------------------------------------------------------- */

const TIERS = [
  {
    key: "ospite",
    name: "OSPITE",
    translation: "Guest",
    threshold: "0 PTS",
    perks: [
      "Welcome bonus: 100 points",
      "1.0× points on every visit",
      "Member-only emails",
    ],
  },
  {
    key: "amico",
    name: "AMICO",
    translation: "Friend",
    threshold: "500 PTS",
    perks: [
      "1.25× points on every visit",
      "Birthday surprise",
      "Early access to events",
    ],
  },
  {
    key: "famiglia",
    name: "FAMIGLIA",
    translation: "Family",
    threshold: "2,000 PTS",
    perks: [
      "1.5× points on every visit",
      "Birthday bonus +500 pts",
      "Complimentary antipasto on visit 5+",
    ],
  },
  {
    key: "padrone",
    name: "PADRONE",
    translation: "Patron",
    threshold: "5,000 PTS",
    perks: [
      "2.0× points on every visit",
      "Free dessert every visit",
      "Priority reservations",
      "Annual chef's table invitation",
    ],
    highlight: true,
  },
];

const TierCard = ({ tier }) => {
  const isHi = !!tier.highlight;
  return (
    <Reveal
      testId={`tier-card-${tier.key}`}
      className={`relative bg-napoli-espresso-soft p-8 flex flex-col h-full ${
        isHi
          ? "border-2 border-napoli-gold lg:scale-[1.03] shadow-[0_30px_60px_-30px_rgba(197,160,77,0.4)]"
          : "border border-napoli-gold/40"
      }`}
      style={
        isHi
          ? {
              backgroundImage:
                "linear-gradient(160deg, #3A2D24 0%, #3A2D24 60%, rgba(139,30,44,0.25) 100%)",
            }
          : undefined
      }
    >
      {isHi ? (
        <span
          data-testid="tier-padrone-ribbon"
          className="absolute -top-3 right-4 bg-napoli-gold text-napoli-espresso font-body font-semibold text-[10px] tracking-[0.28em] px-3 py-1"
        >
          RESERVED
        </span>
      ) : null}

      <Star
        size={20}
        strokeWidth={1.3}
        className={isHi ? "text-napoli-gold" : "text-napoli-gold/60"}
      />

      <h3 className="mt-5 font-display font-bold text-[28px] text-napoli-cream tracking-wider">
        {tier.name}
      </h3>
      <p className="mt-1 font-body italic text-[14px] text-napoli-cream/60">
        {tier.translation}
      </p>
      <p className="mt-5 font-body font-semibold text-[11px] tracking-[0.3em] text-napoli-gold">
        {tier.threshold}
      </p>

      <span aria-hidden className="mt-6 block h-px bg-napoli-gold/30" />

      <ul className="mt-6 space-y-2.5 flex-1">
        {tier.perks.map((p) => (
          <li
            key={p}
            className="font-body text-[13px] text-napoli-cream/80 leading-relaxed flex gap-2"
          >
            <span className="text-napoli-gold mt-[2px]">•</span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </Reveal>
  );
};

const Livelli = () => (
  <section
    data-testid="landing-livelli"
    className="bg-napoli-espresso text-napoli-cream py-16 sm:py-24 px-6"
  >
    <div className="mx-auto max-w-6xl">
      <Reveal className="text-center">
        <Eyebrow color="gold" testId="livelli-eyebrow">I Livelli</Eyebrow>
        <h2 className="mt-4 font-display font-bold text-napoli-cream text-4xl sm:text-5xl">
          Four ways to belong.
        </h2>
        <p className="mt-4 font-body italic text-base text-napoli-cream/70">
          From your first visit to your hundredth.
        </p>
      </Reveal>

      <div
        data-testid="livelli-grid"
        className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        {TIERS.map((t) => (
          <TierCard key={t.key} tier={t} />
        ))}
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  SECTION 6 — Riserva il tuo posto                                          */
/* -------------------------------------------------------------------------- */

const Riserva = () => (
  <section
    data-testid="landing-riserva"
    className="grid grid-cols-1 lg:grid-cols-2 min-h-[560px]"
  >
    {/* LEFT: wine image */}
    <div className="relative min-h-[260px] lg:min-h-0 overflow-hidden">
      <img
        src={WINE_IMAGE}
        alt="A glass of red wine, dark moody lighting."
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, rgba(139,30,44,0.45) 0%, rgba(31,22,17,0.45) 100%)",
        }}
      />
      <p
        className="absolute bottom-6 left-6 font-body italic text-[12px] tracking-wide"
        style={{ color: "rgba(245,235,218,0.7)" }}
      >
        House Chianti, family-bottled.
      </p>
    </div>

    {/* RIGHT: cream + grain + content */}
    <div className="relative bg-napoli-cream text-napoli-espresso flex items-center justify-center px-6 py-16 sm:p-16 overflow-hidden">
      <PaperGrain />
      <Reveal className="relative z-10 max-w-[440px] w-full">
        <Eyebrow testId="riserva-eyebrow">Riserva il tuo posto</Eyebrow>
        <h2 className="mt-4 font-display font-bold text-napoli-espresso leading-none text-5xl sm:text-6xl">
          Pull up a chair.
        </h2>
        <p className="mt-6 font-body text-[17px] text-napoli-espresso-soft leading-relaxed">
          Join Napoli Rewards in under 60 seconds. No app, no card, no fuss —
          just a QR you&apos;ll learn to love.
        </p>

        <div className="mt-10 flex flex-col gap-4">
          <Link
            to="/signup"
            data-testid="riserva-cta-join"
            className="block w-full text-center bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[13px] tracking-widest uppercase py-4 hover:bg-napoli-oxblood-dark transition-colors duration-300"
          >
            Join Napoli Rewards
          </Link>
          <Link
            to="/login"
            data-testid="riserva-cta-signin"
            className="self-start font-body text-[13px] text-napoli-oxblood underline underline-offset-4 decoration-napoli-oxblood/60 hover:decoration-napoli-oxblood transition-colors"
          >
            Already a member? Sign in.
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Footer                                                                    */
/* -------------------------------------------------------------------------- */

const FooterColHeading = ({ children }) => (
  <p className="font-body font-semibold text-[11px] tracking-[0.3em] uppercase text-napoli-gold">
    {children}
  </p>
);

const FooterLink = ({ to, children }) => (
  <Link
    to={to}
    className="font-body text-[14px] text-napoli-cream/80 hover:text-napoli-gold transition-colors"
  >
    {children}
  </Link>
);

const Footer = () => (
  <footer
    data-testid="landing-footer"
    className="bg-napoli-espresso text-napoli-cream py-16 px-6"
  >
    <div className="mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-3 gap-12">
      <div>
        <BrandMark size="md" theme="dark" to="/" linkTestId="landing-footer-brand-link" />
        <p className="mt-5 font-body italic text-[13px] text-napoli-cream/60">
          Brooklyn-born. Naples-rooted.
        </p>
      </div>

      <div>
        <FooterColHeading>Visit</FooterColHeading>
        <p className="mt-4 font-body text-[14px] text-napoli-cream/80 leading-relaxed">
          240 Bedford Avenue
          <br />
          Brooklyn, NY 11211
        </p>
        <p className="mt-4 font-body text-[14px] text-napoli-cream/80 leading-relaxed">
          Tuesday – Sunday
          <br />
          5:30 pm – 11:00 pm
        </p>
        <p className="mt-4 font-body text-[14px] text-napoli-cream/80">
          (718) 555-0182
        </p>
      </div>

      <div>
        <FooterColHeading>Rewards</FooterColHeading>
        <ul className="mt-4 space-y-2.5">
          <li><FooterLink to="/login">Sign In</FooterLink></li>
          <li><FooterLink to="/signup">Join</FooterLink></li>
          <li><FooterLink to="/#programma">How It Works</FooterLink></li>
          <li><FooterLink to="/#livelli">FAQ</FooterLink></li>
        </ul>
      </div>
    </div>

    <div className="mx-auto max-w-6xl mt-12 pt-6 border-t border-napoli-gold/20 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
      <p className="font-body text-[11px] text-napoli-cream/40 tracking-wide">
        © 2026 Napoli Cucina · Brooklyn
      </p>
      <p className="font-body text-[11px] text-napoli-cream/40 tracking-wide">
        Powered by Oculus AI Solutions
      </p>
    </div>
  </footer>
);

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

const LandingPage = () => (
  <div data-testid="landing-page" className="bg-napoli-cream">
    <Hero />
    <Storia />
    <Cucina />
    <Programma />
    <Livelli />
    <Riserva />
    <Footer />
  </div>
);

export default LandingPage;
